import dayjs from 'dayjs';
import logger from '../../utils/logger';
import { appInitiated } from '../slice/appSlice';
import { AuthState } from '../../types/auth';
import { checkInvalidStartDate } from '../../utils/cart';
import { CheckoutState, SaveFormDataPayload } from '../../types/checkout-state';
import { cloneDeep } from 'lodash-es';
import { DataLayerEventName } from '../../types/data-layer';
import { DELIVERY_FEE, PICKUP_FEE, rentalProtections, shifts } from '../../constants/cart';
import { DeliveryFormModel, JobsiteFormModel } from '../../types/checkout-form';
import { dispatch, RootState } from '../store';
import { EMPTY_ACTION } from './emptyAction';
import { Epic, ofType } from 'redux-observable';
import { ERROR_ACTION } from './errorAction';
import { getIsTaxExempt, newCart, overwriteCart } from '../../apis/cart';
import { getPriceCatalogKey } from '../../utils/equipment';
import { getQuote } from '../../apis/quotes';
import { getRentalProtectionPlans, getRentalShifts } from '../../apis/equipment';
import { getStateAbbreviation } from '../../utils/location';
import { isAuthInitialized } from '../../utils/authUtils';
import { isValidCoordinates } from '../../utils/profile';
import { PriceQueue } from '../../types/equipment';
import { pushEventToDataLayer } from '../../utils/dataLayerUtils';
import { QuoteRequestEquipmentParams, QuoteRequestParams } from '../../types/quotes';
import { setAuthIdentity, unsetAuthIdentity } from '../slice/authSlice';
import { USStates } from '../../constants/states';
import {
  catchError,
  filter,
  map,
  MonoTypeOperatorFunction,
  Observable,
  of,
  switchMap,
  take,
  tap,
  throttleTime,
} from 'rxjs';
import {
  fetchPriceRequest,
  PricesState,
  refetchPrices,
  updatePriceCatalog,
} from '../slice/pricesSlice';
import {
  modeOfDeliveryChange,
  modeOfReturningChange,
  resetJobsiteDetails,
  saveFormData,
} from '../slice/checkoutSlice';
import {
  LocationState,
  selectLocationFulfilled,
  selectLocationRejected,
} from '../slice/locationSlice';
import {
  CartState,
  RentalProtectionOption,
  ShiftOption,
  SetCartItemPayload,
} from '../../types/cart-state';
import {
  IsTaxExemptRequestParams,
  CartBackendSyncStatus,
  UpdateCartRequest,
} from '../../types/cart';
import {
  DeliveryOptionType,
  DropOffOptionType,
  RentalProtectionType,
} from '../../types/checkout-common';
import {
  setUserUuid,
  setBackendSyncLoading,
  setBackendSyncFulfilled,
  setBackendSyncFailed,
  setQuote,
  setQuoteLoading,
  setQuoteError,
  setCartItem,
  setShift,
  setActiveShift,
  setRentalProtection,
  setActiveRentalProtection,
  setSiteLevelDate,
  setSiteLevelStartDate,
  setSiteLevelEndDate,
  validateAndSetDates,
  setTaxExempt,
  setTaxExemptLoading,
  activateCart,
  onCartActivated,
} from '../slice/cartSlice';

const THROTTLE_TIME = 100;

const isCartActivated =
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (state$: Observable<any>): MonoTypeOperatorFunction<any> =>
    filter(() => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const state = (state$ as any).value.cart as CartState;
      return state.isCartActivated;
    });

// Triggers other side effects on cart activation
export const onCartActivationEpic: Epic = (action$) =>
  action$.pipe(
    ofType(activateCart.type),
    take(1),
    map(() => {
      return onCartActivated();
    }),
    catchError(() => of(EMPTY_ACTION('From onCartActivationEpic: Error'))),
  );

export const syncCartWithBackendEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(onCartActivated.type, setCartItem.type, validateAndSetDates.type),
    isCartActivated(state$),
    filter(() => {
      const cart: CartState = state$.value.cart;
      // Prevent a POST request to the cart API if the cart is empty
      // and the user has no existing cart.
      if (!cart.userUuid && cart.cartItems.length === 0) {
        return false;
      }

      const isInvalidStartDate = cart.cartItems.some((item) =>
        checkInvalidStartDate(item.startDate, cart.timezone),
      );
      if (isInvalidStartDate) {
        return false;
      }

      return true;
    }),
    tap(() => {
      dispatch(
        setBackendSyncLoading({
          status: CartBackendSyncStatus.IS_LOADING,
        }),
      );
    }),
    throttleTime(THROTTLE_TIME, undefined, { leading: false, trailing: true }),
    switchMap(async () => {
      try {
        const branch = state$.value.location.branch?.location;
        const state: CartState = cloneDeep(state$.value.cart);
        const requestBody: UpdateCartRequest = {
          cart_details: state.cartItems.map((item) => {
            return {
              delivery_date: item.startDate,
              return_date: item.endDate,
              equipment_class_id: item.equipmentId,
              equipment_img_url: item.imageSource,
              equipment_name: item.name,
              quantity: item.quantity,
              slug: item.slug,
            };
          }),
          job_site: branch?.city ?? null,
          user_timezone: state.timezone,
        };
        const userUuid = state.userUuid;
        if (userUuid === null) {
          const response = await newCart(requestBody);
          if (response) {
            dispatch(
              setUserUuid({
                id: String(response.user_uuid),
              }),
            );
          }
        } else {
          await overwriteCart(requestBody, userUuid);
        }

        return setBackendSyncFulfilled();
      } catch (error) {
        logger('error', error);
        return setBackendSyncFailed();
      }
    }),
    catchError(() => of(setBackendSyncFailed())),
  );

export const updateQuoteEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(
      appInitiated.type,
      setShift.type,
      setActiveShift.type,
      setRentalProtection.type,
      setActiveRentalProtection.type,
      modeOfDeliveryChange.type,
      modeOfReturningChange.type,
      updatePriceCatalog.type,
      validateAndSetDates.type,
      setCartItem.type,
      setTaxExempt.type,
      saveFormData.type,
    ),
    isCartActivated(state$),
    throttleTime(THROTTLE_TIME, undefined, { leading: false, trailing: true }),
    switchMap(async () => {
      try {
        const state: RootState = cloneDeep(state$.value);
        const cart: CartState = state.cart;
        const location: LocationState = state.location;
        const prices: PricesState = state.prices;
        const equipments: QuoteRequestEquipmentParams[] = [];
        const branchId = location.branch?.branch_id;

        if (
          cart.loadingFlags.isLoadingShift ||
          cart.loadingFlags.isLoadingRPP ||
          cart.loadingFlags.isLoadingTaxExemptStatus
        ) {
          return setQuoteLoading();
        }

        if (!branchId || !cart.shift?.active?.plan || !cart.rentalProtection?.active) {
          return setQuoteError();
        }

        pushEventToDataLayer({
          type: DataLayerEventName.PRICE_UPDATED,
          payload: { total: cart?.quote?.total },
        });

        const priceCatalog = cloneDeep(prices.priceCatalog);
        const missingPrices: PriceQueue = {};
        cart.cartItems.forEach((e) => {
          const priceCatalogKey = getPriceCatalogKey(e.equipmentId);
          if (!priceCatalog[priceCatalogKey]?.price) {
            missingPrices[priceCatalogKey] = {
              equipmentId: e.equipmentId,
              branchId: branchId,
            };
          }
        });

        if (Object.keys(missingPrices).length > 0) {
          Object.values(missingPrices).forEach((e) => {
            dispatch(fetchPriceRequest({ equipmentId: e.equipmentId }));
          });

          return setQuoteLoading();
        }

        if (!isAuthInitialized(state.auth.loginStatus)) {
          return setQuoteError();
        }

        //TODO
        //Passing a default value 1 for day_rate, week_rate and four_week_rate
        //as currently rental-rate api does not guarantee price for all items.
        //Remove this when rental-rate api is updated.

        // Setting the time for start date as start of day i.e. 00:00:00 and end date as end of day i.e. 23:59:00
        // to ensure proper quote calculation. If time is kept the same, it'd miss 1 day in calcualtion
        cart.cartItems.forEach((e) => {
          const priceCatalogKey = getPriceCatalogKey(e.equipmentId);
          const priceEntry = priceCatalog[priceCatalogKey];
          if (priceEntry) {
            equipments.push({
              equipment_class_id: e.equipmentId,
              start_date: dayjs.unix(e.startDate).local().format('YYYY-MM-DDT00:00:00'),
              end_date: dayjs.unix(e.endDate).local().format('YYYY-MM-DDT23:59:59'),
              quantity: e.quantity,
              day_rate: priceEntry.price?.price_per_day ?? 1,
              week_rate: priceEntry.price?.price_per_week ?? 1,
              four_week_rate: priceEntry.price?.price_per_month ?? 1,
            });
          }
        });

        const jobsiteDetails = state.checkout.stepper.jobsiteDetails;
        const { jobsiteLocationId, latitudeLongitude } =
          (jobsiteDetails.formData as JobsiteFormModel) ?? {};
        let jobsiteLatitude, jobsiteLongitude;
        if (latitudeLongitude) {
          const coordinatesDetails = isValidCoordinates(latitudeLongitude);
          if (coordinatesDetails.valid) {
            jobsiteLatitude = coordinatesDetails.latitude;
            jobsiteLongitude = coordinatesDetails.longitude;
          }
        }

        const deliveryDetails = state.checkout.stepper.deliveryDetails;
        const { modeOfDelivery, modeOfReturning } =
          (deliveryDetails.formData as DeliveryFormModel) ?? {};

        const params: QuoteRequestParams = {
          branch_id: location.branch?.branch_id ?? 0,
          equipment_types: equipments,
          shift_multiplier: cart.shift.active.plan.multiplier,
          rpp_id: cart.rentalProtection.active.plan?.rental_protection_plan_id ?? null,
          delivery_fee: modeOfDelivery == DeliveryOptionType.PAID ? DELIVERY_FEE : undefined,
          pickup_fee: modeOfReturning == DropOffOptionType.PAID ? PICKUP_FEE : undefined,
          is_tax_exempt: cart.isTaxExempt,
          jobsite_id: jobsiteLocationId,
          latitude: jobsiteLatitude,
          longitude: jobsiteLongitude,
        };

        if (equipments?.length === 0) {
          return setQuoteError();
        }
        dispatch(setQuoteLoading());
        const quote = await getQuote(params);
        return setQuote({ quote });
      } catch (error) {
        logger('error', error);
        return setQuoteError();
      }
    }),
    catchError((error) => {
      logger('error', 'at updateQuoteEpic while updating quote', error);
      return of(setQuoteError());
    }),
  );

export const updateShiftEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(appInitiated.type, onCartActivated.type),
    isCartActivated(state$),
    tap(() => {
      const state: CartState = cloneDeep(state$.value.cart);
      const activePlan = state.shift?.active;
      const defaultPlan = shifts.find((e) => e.plan?.multiplier == 1);
      dispatch(
        setShift({
          shift: {
            options: shifts,
            active: activePlan ?? defaultPlan,
          },
          isLoading: true,
        }),
      );
    }),
    throttleTime(THROTTLE_TIME, undefined, { leading: false, trailing: true }),
    switchMap(async () => {
      const state: CartState = cloneDeep(state$.value.cart);
      const activePlan = state.shift?.active;

      const response = await getRentalShifts();

      const options: ShiftOption[] = shifts.map((e1) => {
        const plan = response.find((e2) => e2.name === e1.valueAccessKey);
        return { ...e1, plan };
      });
      const defaultPlan = options.find((e) => e.plan?.multiplier == 1);
      const activePlanFound = options.find((e) => e.plan?.id === activePlan?.plan?.id);

      if (!activePlanFound) {
        dispatch(
          setActiveShift({
            active: defaultPlan ?? null,
          }),
        );
      }
      return setShift({
        shift: {
          options: options,
        },
        isLoading: false,
      });
    }),
    catchError((error) => {
      logger('error', 'at updateShiftEpic while loading shifts', error);
      return of(
        setShift({
          shift: {
            options: shifts,
          },
          isLoading: false,
        }),
      );
    }),
  );

export const updateRentalProtectionPlanEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(appInitiated.type, onCartActivated.type),
    isCartActivated(state$),
    tap(() => {
      const state: CartState = cloneDeep(state$.value.cart);
      const activePlan = state.rentalProtection?.active;
      const standardPlan = rentalProtections.find((e) => e.type == RentalProtectionType.STANDARD);
      dispatch(
        setRentalProtection({
          rentalProtection: {
            options: rentalProtections,
            active: activePlan ?? standardPlan,
          },
          isLoading: true,
        }),
      );
    }),
    throttleTime(THROTTLE_TIME, undefined, { leading: false, trailing: true }),
    switchMap(async () => {
      const state: CartState = cloneDeep(state$.value.cart);
      let standardProtection: RentalProtectionOption | undefined;

      const response = await getRentalProtectionPlans();

      const standardProtectionPlan = response.find((e) => e.name == 'Standard RPP');
      const options: RentalProtectionOption[] = rentalProtections.map((e1) => {
        if (e1.type === RentalProtectionType.STANDARD) {
          standardProtection = {
            ...e1,
            plan: standardProtectionPlan,
          };
          return standardProtection;
        }
        return { ...e1 };
      });

      if (
        !state.rentalProtection?.active ||
        state.rentalProtection?.active?.type === RentalProtectionType.STANDARD
      ) {
        dispatch(
          setActiveRentalProtection({
            active: standardProtection ?? null,
          }),
        );
      }
      return setRentalProtection({
        rentalProtection: {
          options,
        },
        isLoading: false,
      });
    }),
    catchError((error) => {
      logger('error', 'at updateRentalProtectionPlanEpic while loading rpp', error);
      return of(
        setRentalProtection({
          rentalProtection: {
            options: rentalProtections,
          },
          isLoading: false,
        }),
      );
    }),
  );

// Validates the following
// - Check validity of dates
// - Creates payload if date needs to be updated
export const validateCartEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(
      appInitiated.type,
      onCartActivated.type,
      setCartItem.type,
      setSiteLevelDate.type,
      setSiteLevelStartDate.type,
      setSiteLevelEndDate.type,
    ),
    map((action) => {
      let startDate: number | undefined;
      let endDate: number | undefined;
      let isSiteLevelDateChange: boolean = false;

      if (action.type === appInitiated.type) {
        return validateAndSetDates();
      }

      if (action.type === setCartItem.type) {
        const payload = (action.payload as SetCartItemPayload).cartUpdateObject;
        const siteLevelDate = (state$.value.cart as CartState).siteLevelDate;
        const isCartItemDateUpdated =
          siteLevelDate?.start !== payload?.startDate || siteLevelDate?.end !== payload?.endDate;
        if (isCartItemDateUpdated) {
          startDate = payload?.startDate;
          endDate = payload?.endDate;
        }
      } else {
        isSiteLevelDateChange = true;
      }

      if ((startDate && endDate) || isSiteLevelDateChange) {
        return validateAndSetDates({ isSiteLevelDateChange, startDate, endDate });
      }
      return EMPTY_ACTION('At validateCartEpic: No date change detected');
    }),
    catchError(() => of(EMPTY_ACTION('Error at validateCartEpic'))),
  );

export const isTaxExemptEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(
      onCartActivated.type,
      selectLocationFulfilled.type,
      selectLocationRejected.type,
      setAuthIdentity.type,
      unsetAuthIdentity.type,
      saveFormData.type,
      resetJobsiteDetails.type,
    ),
    isCartActivated(state$),
    filter((action$) => {
      // For checkout state changes, trigger epic only when jobsiteDetails is updated
      if (action$.type === saveFormData.type) {
        return (action$.payload as SaveFormDataPayload).stepKey === 'jobsiteDetails';
      }
      // For location state changes, trigger epic only if jobsite id is not present
      if (
        action$.type === selectLocationFulfilled.type ||
        action$.type === selectLocationRejected.type
      ) {
        const jobsiteStep = (state$.value.checkout as CheckoutState).stepper.jobsiteDetails;
        const jobsiteId = (jobsiteStep.formData as JobsiteFormModel)?.jobsiteLocationId;

        return typeof jobsiteId == 'number' ? false : true;
      }
      return true;
    }),
    throttleTime(THROTTLE_TIME, undefined, { leading: false, trailing: true }),
    switchMap(async () => {
      const authState = state$.value.auth as AuthState;
      const locationState = state$.value.location as LocationState;

      const jobsiteStep = (state$.value.checkout as CheckoutState).stepper.jobsiteDetails;
      const jobsiteId = (jobsiteStep.formData as JobsiteFormModel)?.jobsiteLocationId;

      if (
        !authState.authContext ||
        !locationState.branch ||
        (!locationState.userLocation && !jobsiteId)
      ) {
        return setTaxExempt(false);
      }

      try {
        let params: IsTaxExemptRequestParams | undefined;

        if (typeof jobsiteId === 'number') {
          params = { jobsiteId };
        } else {
          const stateAbbreviation = locationState.userLocation?.state
            ? getStateAbbreviation(locationState.userLocation.state, USStates)
            : undefined;
          if (stateAbbreviation) {
            params = { stateAbbreviation };
          }
        }

        if (!params) {
          logger('error', 'at isTaxExemptEpic : missing jobsite and state abbreviation values');
          return setTaxExempt(false);
        }

        dispatch(setTaxExemptLoading());

        const isTaxExemptResponse = await getIsTaxExempt(
          authState.authContext.companyId,
          Number(locationState.branch.branch_id),
          params,
        );
        return setTaxExempt(isTaxExemptResponse.is_tax_exempt);
      } catch (error) {
        logger('error', 'at isTaxExemptEpic', error);
        dispatch(setTaxExempt(false));
        return ERROR_ACTION('Error at isTaxExemptEpic: trycatch');
      }
    }),
    catchError(() => of(ERROR_ACTION('Error at isTaxExemptEpic: catchError'))),
  );

export const refetchCartPricesEpic: Epic = (action$, state$) =>
  action$.pipe(
    ofType(refetchPrices.type),
    switchMap(async () => {
      const state: CartState = cloneDeep(state$.value.cart);
      state.cartItems.forEach((item) => {
        dispatch(fetchPriceRequest({ equipmentId: item.equipmentId }));
      });

      return EMPTY_ACTION('Refetched all cart item prices due to price mismatch');
    }),
    catchError(() => of(EMPTY_ACTION('From refetchCartPricesEpic: Error'))),
  );
